import { MUITextField } from "@abb/arcadia-ui-react"
import { JSX } from "react"
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  FieldPath,
  FieldValues,
} from "react-hook-form"
import { FieldRow, FormPage } from "../../components/FormPage"

type FieldRenderer<T extends FieldValues, P extends FieldPath<T>> = (props: {
  field: ControllerRenderProps<T, P>
}) => JSX.Element

type TopicBaseFields = {
  applicationId: string
  name: string
  context?: string
  usefulLink?: string
  priority?: number
}

interface TopicFormProps<T extends TopicBaseFields & FieldValues> {
  returnURL: string
  onSubmit: () => void
  isSubmitting: boolean
  isDirty: boolean
  isValid: boolean
  control: Control<T>
  errors: FieldErrors<T>
  includeContext?: boolean
  includeUsefulLink?: boolean
  includePriority?: boolean
  renderApplicationSelect: FieldRenderer<T, "applicationId" & FieldPath<T>>
  renderNameField: FieldRenderer<T, "name" & FieldPath<T>>
  renderContextField?: FieldRenderer<T, "context" & FieldPath<T>>
  renderUsefulLinkField?: FieldRenderer<T, "usefulLink" & FieldPath<T>>
  renderPriorityField?: FieldRenderer<T, "priority" & FieldPath<T>>
}

function getErrorMessage(error: unknown): string {
  if (!error) {
    return ""
  }
  if (typeof error === "string") {
    return error
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return ""
}

export function TopicForm<T extends TopicBaseFields & FieldValues>({
  returnURL,
  onSubmit,
  isSubmitting,
  isDirty,
  isValid,
  control,
  errors,
  includeContext = false,
  includeUsefulLink = false,
  includePriority = false,
  renderApplicationSelect,
  renderNameField,
  renderContextField,
  renderUsefulLinkField,
  renderPriorityField,
}: Readonly<TopicFormProps<T>>) {
  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application">
        <>
          <Controller<T, "applicationId" & FieldPath<T>>
            name={"applicationId" as "applicationId" & FieldPath<T>}
            control={control}
            render={renderApplicationSelect}
          />
          {errors.applicationId && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                applicationId: {
                  message: getErrorMessage(errors.applicationId?.message),
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Name">
        <Controller<T, "name" & FieldPath<T>>
          name={"name" as "name" & FieldPath<T>}
          control={control}
          render={renderNameField}
        />
      </FieldRow>
      {includeContext && renderContextField && (
        <FieldRow label="Context">
          <Controller<T, "context" & FieldPath<T>>
            name={"context" as "context" & FieldPath<T>}
            control={control}
            render={renderContextField}
          />
        </FieldRow>
      )}
      {includeUsefulLink && renderUsefulLinkField && (
        <FieldRow label="Useful Link">
          <Controller<T, "usefulLink" & FieldPath<T>>
            name={"usefulLink" as "usefulLink" & FieldPath<T>}
            control={control}
            render={renderUsefulLinkField}
          />
        </FieldRow>
      )}
      {includePriority && renderPriorityField && (
        <FieldRow label="Priority">
          <Controller<T, "priority" & FieldPath<T>>
            name={"priority" as "priority" & FieldPath<T>}
            control={control}
            render={renderPriorityField}
          />
        </FieldRow>
      )}
    </FormPage>
  )
}
